import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Baby, Heart, ChevronRight } from "lucide-react";
import { format, parseISO } from "date-fns";

/** Breeding history card — shows heat cycles + litters where this dog is the
 * dam. Rendered on the Overview page for female dogs and for any dog that
 * already has linked breeding records (so switching a dog's sex later doesn't
 * make history disappear).
 */
export default function BreedingHistoryCard({ dog, isBreeder }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isFemale = (dog?.sex || "").toLowerCase().startsWith("f");

  useEffect(() => {
    let alive = true;
    if (!dog?.id) return;
    setLoading(true);
    api.get(`/dogs/${dog.id}/breeding-summary`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dog?.id]);

  if (loading) return null;
  const stats = data?.stats || { heat_cycle_count: 0, litter_count: 0, puppy_count_total: 0 };
  const hasAny = stats.heat_cycle_count > 0 || stats.litter_count > 0;

  // Show empty-state CTA only for female dogs of breeders. For non-breeders
  // and males with no linked records, hide entirely to keep Overview clean.
  if (!hasAny && !(isFemale && isBreeder)) return null;

  const cycles = data?.heat_cycles || [];
  const litters = data?.litters || [];

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="breeding-history-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Heart size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Breeding history</p>
              <p className="font-medium text-base">{dog.name}&apos;s linked cycles &amp; litters</p>
            </div>
          </div>
        </div>

        {!hasAny ? (
          <div className="rounded-md bg-muted/40 border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="breeding-history-empty">
            <p>No heat cycles or litters linked to {dog.name} yet.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Link to="/breeder/heat-cycles">
                <Button size="sm" variant="outline" data-testid="breeding-history-log-cycle">
                  <Heart size={14} className="mr-1.5" /> Log a heat cycle
                </Button>
              </Link>
              <Link to="/breeder/litters">
                <Button size="sm" variant="outline" data-testid="breeding-history-plan-litter">
                  <Baby size={14} className="mr-1.5" /> Plan a litter
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <StatBlock label="Cycles" value={stats.heat_cycle_count} testid="breeding-stat-cycles" />
              <StatBlock label="Litters" value={stats.litter_count} testid="breeding-stat-litters" />
              <StatBlock label="Puppies" value={stats.puppy_count_total} testid="breeding-stat-puppies" />
            </div>

            {litters.length > 0 && (
              <div className="mb-4" data-testid="breeding-history-litters">
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Recent litters</p>
                <ul className="space-y-1.5">
                  {litters.slice(0, 4).map((l) => {
                    const label = [l.dam_name || dog.name, l.stud_name].filter(Boolean).join(" × ") || "Litter";
                    const dateStr = l.born_on ? `whelped ${safeDate(l.born_on)}`
                      : l.bred_on ? `bred ${safeDate(l.bred_on)}`
                      : l.is_planned ? "planned" : "";
                    return (
                      <li key={l.id}>
                        <Link
                          to={`/breeder/litters/${l.id}`}
                          className="flex items-center justify-between gap-2 rounded-md px-3 py-2 bg-secondary/40 hover:bg-secondary transition-colors text-sm"
                          data-testid={`breeding-history-litter-${l.id}`}
                        >
                          <span className="min-w-0 flex-1 truncate">
                            <span className="font-medium">{label}</span>
                            {dateStr && <span className="text-muted-foreground"> · {dateStr}</span>}
                            {l.puppy_count > 0 && (
                              <span className="text-muted-foreground"> · {l.puppy_count} pup{l.puppy_count === 1 ? "" : "s"}</span>
                            )}
                            {l.is_planned && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 text-[10px] uppercase tracking-wider font-semibold">Planned</span>
                            )}
                          </span>
                          <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {cycles.length > 0 && (
              <div data-testid="breeding-history-cycles">
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Recent heat cycles</p>
                <ul className="space-y-1.5">
                  {cycles.slice(0, 3).map((c) => (
                    <li key={c.id}>
                      <Link
                        to="/breeder/heat-cycles"
                        className="flex items-center justify-between gap-2 rounded-md px-3 py-2 bg-secondary/40 hover:bg-secondary transition-colors text-sm"
                        data-testid={`breeding-history-cycle-${c.id}`}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{safeDate(c.cycle_start)}</span>
                          {c.stud_name && !c.is_observation_only && (
                            <span className="text-muted-foreground"> · stud {c.stud_name}</span>
                          )}
                          {c.is_observation_only && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border text-[10px] uppercase tracking-wider font-semibold">Observation</span>
                          )}
                          {c.linked_litter_id && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30 text-[10px] uppercase tracking-wider font-semibold">Litter</span>
                          )}
                        </span>
                        <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatBlock({ label, value, testid }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2.5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold mt-0.5">{value}</p>
    </div>
  );
}

function safeDate(iso) {
  try {
    return format(parseISO(iso), "MMM d, yyyy");
  } catch {
    return iso;
  }
}
